/**
 *  All api related to Bookings
 */

import _ from 'lodash'
import { CancelToken } from 'axios'
import { OmnitechApi } from '../../api'
import { cancelRequest } from '../../helpers'
import { isErrorExist, convertResponseToErrorObject } from '../../omnitech-error'

export function fetchBookings(options = {}) {
  const { page = 1, ...others } = options
  const params = {
    page,
    ...others
  }
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchBookings',
    () => source.cancel('fetchBookings: Operation cancelled by user')
  )
  return OmnitechApi
    .request({
      endpoint: 'consumed_sku_quotas',
      method: 'get',
      params,
      type: 'skuQuota',
      cancelToken: source.token,
      arrayFormat: options.arrayFormat || 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ cacheChecksum, data }) => ({
      consumedSkuQuotas: data.consumedSkuQuotas,
      next: _.get(data, 'consumedSkuQuotasPagination.nextPage') > page
        ? () => fetchBookings({
          page: page + 1,
          ...others
        })
        : null,
      all: page === 1 && _.get(data, 'consumedSkuQuotasPagination.nextPage') > 1
        ? () => Promise.all(
          _.times(
            _.get(data, 'consumedSkuQuotasPagination.totalPages') - 1,
            (index) => fetchBookings({
              pageCountless: true,
              page: index + 2,
              ...others
            }),
          ),
        )
        : null,
      count: _.get(data, 'consumedSkuQuotasPagination.totalCount'),
    }))
}

export function fetchBooking({ id, ...params }) {
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchBooking',
    () => source.cancel('fetchBooking: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `consumed_sku_quotas/${id}`,
      method: 'get',
      params,
      type: 'skuQuota',
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      consumedSkuQuota: _.get(data, 'consumedSkuQuota', {})
    }))
}

export function fetchBookingByQrCode({ qrCodeValue, ...params }) {
  const source = CancelToken.source()
  cancelRequest.add(
    'fetchBookingByQrCode',
    () => source.cancel('fetchBookingByQrCode: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `consumed_sku_quotas/by_qr_code/${encodeURIComponent(qrCodeValue)}`,
      method: 'get',
      params,
      type: 'skuQuota',
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      consumedSkuQuota: _.get(data, 'consumedSkuQuota', {})
    }))
}

export function createBookingCancel({ id, ...others }) {
  const params = {
    ...others
  }

  if (_.isEmpty((id || '').toString())) {
    console.error('createBookingCancel: missing parameter "id"')
    return Promise.reject()
  }

  const source = CancelToken.source()
  cancelRequest.add(
    'createBookingCancel',
    () => source.cancel('createBookingCancel: Operation cancelled by user')
  )

  return OmnitechApi
    .request({
      endpoint: `consumed_sku_quotas/${id}/cancel/`,
      method: 'post',
      data: {
        data: {
          consumedSkuQuota: {},
        },
      },
      params,
      type: 'skuQuota',
      cancelToken: source.token,
      arrayFormat: 'brackets',
    })
    .catch(error => {
      if (cancelRequest.isCancel(error)) {
        throw cancelRequest.getErrorObject()
      }
      throw convertResponseToErrorObject(_.get(error, 'response.data'), { useResponseMessage: true })
    })
    .then(response => {
      if (isErrorExist(response.data)) throw convertResponseToErrorObject(response.data, { useResponseMessage: true })
      return response.data
    })
    .then(({ data }) => ({
      consumedSkuQuota: _.get(data, 'consumedSkuQuota', {})
    }))
}
